import React, {useEffect, useState, useRef, useCallback} from "react";
import {Trace, Link} from "../../../pages/Traces";
import {TraceIdTitle, TraceRequestResultDetails} from "./TraceTitle";
import {extractAPIPath, fillMissingEntry, getMessageForStatusCode} from "./utils";
import axios from "utility/customAxios";
import {AxiosPromise} from "axios";
import {TraceFlameGraph, TraceFlameGraphHandle} from "./TraceFlameGraph";
import {InfoIcon, ExternalLinkIcon} from "lucide-react";
import {toast} from "../../../components/ui/use-toast";

// The Trace type should be renamed imo to Span but will do that later.
export type Span = Trace

class SpansResponse {
    traces: Span[]

    constructor(spans: Span[]) {
        this.traces = spans;
    }
}

function TracePanelContent(props: {
    endpoint: string,
    method: string,
    statusCode: string,
    filter: Map<string, string[]>,
    setFilter: (filter: Map<string, string[]>) => void,
    excludeFilter: Map<string, string[]>,
    setExcludeFilter: (filter: Map<string, string[]>) => void,
    trace: Trace,
    highlightSpanId?: string
}) {
    const [spans, setSpans] = useState<Span[]>([]);
    const [traceId, setTraceId] = useState<string>(props.trace.traceId);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const panelRef = useRef<HTMLDivElement>(null);
    const [linkedTraceId, setLinkedTraceId] = useState<string | undefined>(undefined);
    const [treeBuilt, setTreeBuilt] = useState<boolean>(false);
    const [extendedTimeRangeUsed, setExtendedTimeRangeUsed] = useState<boolean>(false);
    const spanTreeRef = useRef<TraceFlameGraphHandle>(null);
    

    // Define fetchData outside the useEffect so it can be used in callbacks
    const fetchData = useCallback(async (useExtendedTimeRange = false) => {
        setIsLoading(true);
        try {
            // Calculate time range based on whether we're using extended range or not
            const timeOffset = useExtendedTimeRange ? 3600 : 300; // 1 hour vs 5 minutes

            const d: AxiosPromise<SpansResponse> = axios.post("/api/v1/spans", {
                    "startTime": Math.trunc(props.trace.time / 1000 - timeOffset),
                    "endTime": Math.trunc((props.trace.time + props.trace.duration / 1000 + timeOffset * 1000) / 1000),
                    "traceId": props.trace.traceId,
                    "shouldReturnNonMetoroEpbfSpans": true
                }
            )
            const awaited = (await d).data;
            setSpans(awaited.traces);
            setExtendedTimeRangeUsed(useExtendedTimeRange);
        } catch (e) {
            console.error(e);
            // Show toast notification when API call fails
            toast({
                title: "Error",
                description: "Cannot load the span at this time, please try again later",
                variant: "destructive",
                duration: 3000
            });
            // Fall back to example spans if API call fails
        } finally {
            setIsLoading(false);
        }
    }, [props.trace.time, props.trace.duration, props.trace.traceId]);

    useEffect(() => {
        // Reset state when traceId changes
        setTreeBuilt(false);
        setExtendedTimeRangeUsed(false);

        // Initial fetch with normal time range
        fetchData();
    }, [traceId, fetchData]);

    useEffect(() => {
        let linkedTraceIdToSet = undefined;
        if (spans && spans.length > 0) {
            // Find the root span (has no parent or parent is "")
            const rootSpan = spans.find(span =>
                !span.parentSpanId || span.parentSpanId === ""
            );

            // If the root span exists and its traceId doesn't match the current traceId,
            // it means it's linked (ebpf trace is linked to Otel trace)
            if (rootSpan && rootSpan.resourceAttributes.traceId !== props.trace.traceId) {
                linkedTraceIdToSet = rootSpan.resourceAttributes.traceId;
            }
        }
        setLinkedTraceId(linkedTraceIdToSet)
    }, [spans, props.trace.traceId]);

    // Force a resize event when the panel is opened to ensure correct layout
    useEffect(() => {
        if (panelRef.current) {
            const resizeEvent = new Event('resize');
            window.dispatchEvent(resizeEvent);
        }
    }, [panelRef.current, spans]);

    const handleTreeBuildFailed = useCallback(() => {
        // Only make the extended time range call if we haven't already tried it
        if (!extendedTimeRangeUsed) {
            console.log("Tree build failed, trying with extended time range");
            fetchData(true); // Call with extended time range
        }
    }, [extendedTimeRangeUsed, fetchData]);

    const handleTreeBuildSuccess = useCallback(() => {
        setTreeBuilt(true);
    }, []);

    const handleViewLogs = useCallback(() => {
        // This will be handled by TraceFlameGraph's openTraceLogs function
        if (spanTreeRef.current) {
            spanTreeRef.current.openTraceLogs();
        }
    }, []);


    const handleCopyDeeplink = () => {
        // Create the URL with the current location as base
        const url = new URL(window.location.origin + "/traces");
        const startTime = props.trace.time - (5 * 60 * 1000); // 5 minutes before in milliseconds
        const endTime = props.trace.time + (5 * 60 * 1000); // 5 minutes after in milliseconds

        // Add traceId parameter for deep linking
        url.searchParams.set("traceId", props.trace.traceId);
        url.searchParams.set("startTime", startTime.toString());
        url.searchParams.set("endTime", endTime.toString());
        url.searchParams.set("startEnd", `${startTime},${endTime}`);

        // Preserve existing query parameters if they exist
        const currentParams = new URLSearchParams(window.location.search);
        for (const [key, value] of currentParams.entries()) {
            if (key !== 'traceId' && key !== 'startEnd' && key !== 'startTime' && key !== 'endTime') {  // Don't duplicate parameters we've already set
                url.searchParams.set(key, value);
            }
        }

        // Copy to clipboard
        navigator.clipboard.writeText(url.toString())
            .then(() => {
                toast({
                    title: "Success",
                    description: "Trace link copied to clipboard",
                    duration: 2000
                });
            })
            .catch((err) => {
                console.error('Failed to copy link:', err);
                toast({
                    title: "Error",
                    description: "Failed to copy link to clipboard",
                    variant: "destructive",
                    duration: 2000
                });
            });
    }

    return <div 
                ref={panelRef}
                data-vaul-no-drag
                className="h-full p-4 bg-backgrounddark shadow flex-col justify-start items-center gap-4 inline-flex border-l overflow-hidden">

        <div className={"w-full flex flex-col gap-4 flex-shrink-0"}>
            <div className={"w-full flex justify-between items-end"}>
                <TraceIdTitle traceId={props.trace.traceId}
                            serviceName={props.trace.displayServiceName}
                            method={props.method}
                            path={props.endpoint}
                            spans={spans}
                            linkedTraceId={linkedTraceId}
                />

                <div className={"w-full flex items-end justify-end"}>
                    <TraceRequestResultDetails
                        handleCopyDeeplink={handleCopyDeeplink}
                        datetime={props.trace.time}
                        statusCode={props.statusCode}
                        duration={props.trace.duration}
                        statusMessage={getMessageForStatusCode(props.trace.spanAttributes["http.status_code"])}
                        handleViewLogs={handleViewLogs}
                        logsDisabled={spans.length === 0}
                    />
                </div>
            </div>
            {linkedTraceId && (
                <div className="w-full px-2 py-2 bg-primary/10 rounded border border-primary/30 flex">
                    <InfoIcon className="h-5 w-5 text-primary flex-shrink-0" />
                    <div className="text-sm text-textmedium">
                        <span className="font-medium ml-1 ">Linked Traces:</span> This eBPF trace (<span className="font-medium">{props.trace.traceId.substring(0, 8)}...</span>) is linked to OTel trace (<span className="font-medium">{linkedTraceId.substring(0, 8)}...</span>).
                        The trace tree displayed below belongs to the OTel trace.
                    </div>
                </div>
            )}
        </div>
        <div className={"w-full h-full flex-grow overflow-hidden flex flex-col"}>
            <div className="h-full overflow-hidden">
                {isLoading ? (
                    <div className="flex h-full items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                        <span className="ml-2 text-textmedium">Loading trace details...</span>
                    </div>
                ) : spans.length > 0 ? (
                    <TraceFlameGraph 
                        ref={spanTreeRef}
                        spans={spans} 
                        filter={props.filter}
                        setFilter={props.setFilter}
                        excludeFilter={props.excludeFilter}
                        setExcludeFilter={props.setExcludeFilter}
                        highlightSpanId={props.highlightSpanId}
                        onTreeBuildFailed={() => {
                            // Only make the extended time range call if we haven't already tried it
                            if (!extendedTimeRangeUsed) {
                                console.log("Tree build failed, trying with extended time range");
                                fetchData(true); // Call with extended time range
                            }
                        }}
                        onTreeBuildSuccess={() => {
                            setTreeBuilt(true);
                        }}
                        linkedTraceId={linkedTraceId}
                        traceId={props.trace.traceId}
                    />
                ) : (
                    <div className="flex h-full items-center justify-center text-textmedium">
                        No spans found for this trace
                    </div>
                )}
            </div>
        </div>
    </div>
}

export default TracePanelContent;
